/**
 * DesktopMessagingLayout — Glassmorphism two-column messaging overlay.
 *
 * Positioned to the right of the persistent DesktopNavRail.
 * [Contact List 380px] | [Chat flex-1]
 *
 * The contact list slides in from the left on mount.
 * Left offset tracks the NavRail width (collapsed/expanded).
 */

import { motion } from 'motion/react';
import { MessagingStack } from '@/stackflow';
import { DesktopContactList } from '@/features/messaging/components/DesktopContactList';
import { useLayout } from '@/components/layout/LayoutContext';
import { NAV_RAIL_COLLAPSED, NAV_RAIL_EXPANDED } from '@/components/layout/DesktopNavRail';

export function DesktopMessagingLayout() {
  const { sidebarCollapsed, sidebarSide } = useLayout();
  const navWidth = sidebarCollapsed ? NAV_RAIL_COLLAPSED : NAV_RAIL_EXPANDED;

  return (
    <motion.div
      dir="ltr"
      className="fixed top-0 bottom-0 z-[100] flex overflow-hidden"
      style={{
        ...(sidebarSide === 'right' ? { right: navWidth, left: 0 } : { left: navWidth, right: 0 }),
        transition: 'left 0.3s ease-in-out, right 0.3s ease-in-out',
        backgroundColor: 'var(--user-page-bg)',
      } as React.CSSProperties}
      initial={{ x: -380 }}
      animate={{ x: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    >
      {/* Glass contact sidebar — z-10 keeps it above any fixed-position children in the chat pane */}
      <aside
        className="relative z-10 flex w-[380px] flex-shrink-0 flex-col overflow-hidden"
        style={{
          borderInlineEnd: '1px solid var(--user-page-card-border)',
        }}
      >
        <DesktopContactList />
      </aside>
      {/* Chat (Stackflow) */}
      <div
        className="relative z-0 flex flex-1 flex-col overflow-hidden"
        style={{
          backgroundColor: 'var(--user-page-card-bg)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        }}
      >
        <MessagingStack />
      </div>
    </motion.div>
  );
}
